import React from "react"
import {
  useManagerQuizSettings,
  useUpdateManagerQuizSettings,
} from "@/lib/queries"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { toast } from "sonner"
import type { ManagerQuizSetting } from "@/lib/types"

const PERCENT_OPTIONS = [ 10, 20, 30, 40, 50, 60, 70, 80, 90, 100 ]
const MAX_ATTEMPTS_OPTIONS = [ 1, 2, 3, 4, 5 ]

// Score-to-pass is stored server-side as a raw correct-answer count
// (pass_count), since that is what the rest of the system — grading,
// effective_pass_count, the per-version default — already works in. The
// dropdown only ever shows and picks a percentage; these two functions are
// the only place that conversion happens.
const percentToCount = (percent: number, questionCount: number) => {
  if (questionCount <= 0) return percent
  return Math.min(questionCount, Math.max(1, Math.round((percent / 100) * questionCount)))
}

const countToPercent = (count: number, questionCount: number) => {
  if (questionCount <= 0) return 100
  return Math.min(100, Math.max(10, Math.round((count / questionCount) * 10) * 10))
}

export function SettingsTab() {
  const { data: quizSettings = [], isLoading: quizSettingsLoading } = useManagerQuizSettings()
  const updateQuizSettings = useUpdateManagerQuizSettings()
  const [quizSettingChanges, setQuizSettingChanges] = React.useState<
    Record<number, { pass_count?: string; max_attempts?: string; open_book?: boolean }>
  >({})

  const seedChange = (row: ManagerQuizSetting) => ({
    pass_count: row.pass_count === null ? "" : String(row.pass_count),
    max_attempts: row.max_attempts === null ? "" : String(row.max_attempts),
    open_book: row.open_book,
  })

  const passCountValue = (row: ManagerQuizSetting) =>
    quizSettingChanges[row.quiz_id]?.pass_count ?? (row.pass_count === null ? "" : String(row.pass_count))

  const maxAttemptsValue = (row: ManagerQuizSetting) =>
    quizSettingChanges[row.quiz_id]?.max_attempts ?? (row.max_attempts === null ? "" : String(row.max_attempts))

  const openBookValue = (row: ManagerQuizSetting) =>
    quizSettingChanges[row.quiz_id]?.open_book ?? row.open_book

  const passPercentValue = (row: ManagerQuizSetting) => {
    const raw = passCountValue(row)
    if (raw === "") return "default"
    const count = parseInt(raw, 10)
    if (Number.isNaN(count)) return "default"
    return String(countToPercent(count, row.question_count))
  }

  const handleSettingChange = (quizId: number, field: "pass_count" | "max_attempts", value: string) => {
    setQuizSettingChanges((prev) => {
      const row = quizSettings.find((r) => r.quiz_id === quizId)
      const existing = prev[quizId] ?? (row ? seedChange(row) : {})
      return {
        ...prev,
        [quizId]: { ...existing, [field]: value },
      }
    })
  }

  const handlePassPercentChange = (row: ManagerQuizSetting, percent: string) => {
    if (percent === "default") {
      handleSettingChange(row.quiz_id, "pass_count", "")
      return
    }
    handleSettingChange(row.quiz_id, "pass_count", String(percentToCount(Number(percent), row.question_count)))
  }

  const handleOpenBookChange = (quizId: number, checked: boolean) => {
    setQuizSettingChanges((prev) => {
      const row = quizSettings.find((r) => r.quiz_id === quizId)
      const existing = prev[quizId] ?? (row ? seedChange(row) : {})
      return {
        ...prev,
        [quizId]: { ...existing, open_book: checked },
      }
    })
  }

  const handleSaveQuizSettings = async () => {
    const settings = Object.entries(quizSettingChanges).map(([ quizId, change ]) => {
      const row = quizSettings.find((r) => r.quiz_id === Number(quizId))
      return {
        quiz_id: Number(quizId),
        pass_count: change.pass_count ? parseInt(change.pass_count) : null,
        max_attempts: change.max_attempts ? parseInt(change.max_attempts) : null,
        open_book: change.open_book ?? row?.open_book ?? false,
      }
    })
    if (settings.length === 0) {
      toast.info("No changes to save")
      return
    }
    try {
      await updateQuizSettings.mutateAsync(settings)
      toast.success("Pass criteria updated")
      setQuizSettingChanges({})
    } catch {
      toast.error("Failed to update pass criteria")
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Assessment Settings</CardTitle>
        <CardDescription className="space-y-1">
          <p>
            Update your organization’s assessment settings, including the
            passing score, number of attempts allowed, and whether
            assessments are open book or closed book.
          </p>
          <p>
            Changes will immediately apply to all active and pending
            assessments. Historical completed assessments and their results
            will not be affected.
          </p>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {quizSettingsLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : quizSettings.length === 0 ? (
          <p className="text-sm text-muted-foreground">No assessments available</p>
        ) : (
          <div className="space-y-2">
            {quizSettings.map((row) => (
              <div key={row.quiz_id} className="grid grid-cols-[1fr,auto,auto,auto] items-center gap-4 rounded-lg border p-3">
                <span className="text-sm font-medium">{row.title}</span>
                <div className="grid gap-1">
                  <Label htmlFor={`pass-percent-${row.quiz_id}`} className="text-xs text-muted-foreground">
                    Score to pass
                  </Label>
                  <Select
                    value={passPercentValue(row)}
                    onValueChange={(v) => handlePassPercentChange(row, v)}
                  >
                    <SelectTrigger id={`pass-percent-${row.quiz_id}`} className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">
                        Default (100%)
                      </SelectItem>
                      {PERCENT_OPTIONS.map((p) => (
                        <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1">
                  <Label htmlFor={`max-attempts-${row.quiz_id}`} className="text-xs text-muted-foreground">
                    Max attempts
                  </Label>
                  <Select
                    value={maxAttemptsValue(row) || "unlimited"}
                    onValueChange={(v) =>
                      handleSettingChange(row.quiz_id, "max_attempts", v === "unlimited" ? "" : v)
                    }
                  >
                    <SelectTrigger id={`max-attempts-${row.quiz_id}`} className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unlimited">Unlimited</SelectItem>
                      {MAX_ATTEMPTS_OPTIONS.map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1">
                  <Label htmlFor={`open-book-${row.quiz_id}`} className="text-xs text-muted-foreground">
                    Open book
                  </Label>
                  <div className="flex h-9 items-center gap-2">
                    <Switch
                      id={`open-book-${row.quiz_id}`}
                      checked={openBookValue(row)}
                      disabled={!row.open_book_permitted}
                      onCheckedChange={(checked) => handleOpenBookChange(row.quiz_id, checked)}
                    />
                    <span className="text-sm text-muted-foreground">
                      {row.open_book_permitted
                        ? openBookValue(row) ? "Allowed" : "Not allowed"
                        : "Not available"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {quizSettings.length > 0 && (
          <Button
            onClick={handleSaveQuizSettings}
            disabled={Object.keys(quizSettingChanges).length === 0 || updateQuizSettings.isPending}
          >
            {updateQuizSettings.isPending ? "Saving..." : "Save Settings"}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
